/**
 * Shared market → assets roster for Curvance, written by the public-data
 * fetcher and read by the oracle-price fetcher.
 *
 * Every other oracle fetcher resolves its market list from `data-sdk` config,
 * because those protocols publish a static roster. Curvance does not: its
 * markets are DISCOVERED on-chain (`CentralRegistry.marketManagers()` →
 * `queryTokensListed()`), and `getCalls` is synchronous, so the price fetcher
 * cannot discover them itself.
 *
 * Re-discovering here would also be actively wrong, not merely wasteful: the
 * price pass would be pricing a market list that could differ from the one the
 * data pass just published, so a newly listed market would get rows with no
 * prices or prices with no rows.
 *
 * Cold start: on the very first refresh of a process the roster is empty and
 * this fetcher contributes nothing. That is intentional and safe — Curvance
 * assets simply fall back to the shared price map for one cycle, and the public
 * fetcher fills the roster on the same pass.
 */

export interface CurvanceMarketAssets {
  marketManager: string
  /** Lowercase underlying addresses of the market's legs. */
  assets: string[]
}

const rosterCache = new Map<string, CurvanceMarketAssets[]>()

const key = (chainId: string, lender: string) => `${chainId}:${lender}`

/** Publish the discovered roster. Called by the public-data fetcher. */
export function setCurvanceAssetRoster(
  chainId: string,
  lender: string,
  markets: CurvanceMarketAssets[],
): void {
  // An empty result usually means the read failed rather than that the
  // protocol emptied out, so keep the previous roster instead of blanking the
  // price fetcher for a cycle.
  if (markets.length === 0) return
  rosterCache.set(key(chainId, lender), markets)
}

/** Read the roster. Empty until the public-data fetcher has run once. */
export function getCurvanceAssetRoster(
  chainId: string,
  lender: string,
): CurvanceMarketAssets[] {
  return rosterCache.get(key(chainId, lender)) ?? []
}

/** Test seam. */
export function __resetCurvanceAssetRoster(): void {
  rosterCache.clear()
}
